/**
 * Sign-in route (APP-001 §8).
 *
 * Uses Clerk's hosted <SignIn /> component mounted on the /sign-in path. The
 * return path (and checkout intent) is preserved through the auth flow and
 * validated on success. No backend secrets, no API-key creation here.
 */
import { SignIn, useAuth } from '@clerk/clerk-react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { parseReturnIntent } from '@/auth/returnPath';
import { track } from '@/lib/analytics';
import { AuthCard } from '@/components/navigation/AuthCard';
import { BrandMark } from '@/components/navigation/BrandMark';
import { getAppLinks } from '@/config/links';
import { AuthLegalLinks } from '@/components/navigation/AuthLegalLinks';

export function SignInRoute() {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const intent = parseReturnIntent(location.search);
  const links = getAppLinks();

  useEffect(() => {
    track({ event: 'sign_in_started' });
  }, []);

  // After a successful Clerk sign-in, route to the preserved return path or
  // the dashboard. Checkout intent is preserved in the URL for later packages;
  // APP-001 does NOT initiate billing.
  useEffect(() => {
    if (isSignedIn) {
      track({ event: 'sign_in_completed' });
      navigate(intent.returnPath ?? '/dashboard', { replace: true });
    }
  }, [isSignedIn, intent.returnPath, navigate]);

  if (!isLoaded) return null;
  if (isSignedIn) return <Navigate to={intent.returnPath ?? '/dashboard'} replace />;

  return (
    <AuthCard>
      <div className="mb-6 flex justify-center">
        <a href={links.marketingBase} aria-label="Signup Risk home">
          <BrandMark />
        </a>
      </div>
      <SignIn
        routing="path"
        path="/sign-in"
        fallbackRedirectUrl={intent.returnPath ?? '/dashboard'}
        appearance={{
          elements: { card: 'shadow-none p-0' },
        }}
      />
      <AuthLegalLinks />
    </AuthCard>
  );
}
